import React, { useState } from 'react';
import { Mail, MapPin, Instagram, Send, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { WhatsAppIcon, WHATSAPP_DISPLAY, waLink, Reveal } from './Effects';

const INFO = [
  { icon: Mail, title: 'Customer Care Email', text: 'customercare@fbd-cleaning.com', href: 'mailto:customercare@fbd-cleaning.com' },
  { icon: MapPin, title: 'Warehouse & Office Address', text: '1B-28A/142, WH 1, B BLOCK, New Industrial Twp 1, New Industrial Township, Faridabad, Haryana 121001' },
  { icon: Instagram, title: 'Follow Us on Instagram', text: '@fbd.cleaning', href: 'https://instagram.com/fbd.cleaning' },
  { icon: MapPin, title: 'Locate Our Facility', text: 'Click to get directions on Google Maps', href: 'https://share.google/mfTiw3jz37ZcDKp4W' },
];

const ContactSection = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    toast.success('Message sent successfully! Our team will reach out within 24 hours.');
    setForm({ name: '', phone: '', email: '', message: '' });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-primary font-semibold uppercase tracking-wide text-sm mb-2">Connect With Us</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground">Get in <span className="gradient-text-animated">Touch</span></h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Have questions about our products or want to partner with us? Drop us a line below.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="bg-secondary/40 rounded-2xl p-8 border border-border">
            <h3 className="font-display font-bold text-xl text-foreground mb-1">Email Us</h3>
            <p className="text-muted-foreground text-sm mb-6">We reply within 24 hours.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 bg-white" placeholder="+91 " />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-white" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-white" placeholder="How can we help?" />
              </div>
              <Button data-testid="contact-send-btn" type="submit" className="btn-shimmer w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6">
                <Send className="h-4 w-4 mr-2" /> Send Message
              </Button>
            </form>
          </div>

          <div className="space-y-5">
            <Reveal>
              <div data-testid="contact-whatsapp-card" className="contact-card group rounded-2xl p-6 border border-[#25D366]/30 bg-gradient-to-br from-[#25D366]/10 to-white flex items-center gap-4">
                <a href={waLink('Hi FBD Cleaning, I would like to know more about your products')} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 flex-1">
                  <div className="p-3 rounded-xl bg-[#25D366] text-white shadow-lg shadow-[#25D366]/30 group-hover:rotate-[-8deg] transition-transform"><WhatsAppIcon className="h-6 w-6" /></div>
                  <div>
                    <p className="font-semibold text-foreground">Chat / Call Us on WhatsApp</p>
                    <p data-testid="contact-phone-number" className="font-display font-extrabold text-xl text-[#128C7E] tracking-wide">{WHATSAPP_DISPLAY}</p>
                    <p className="text-xs text-muted-foreground">Mon–Sat · 9 AM – 7 PM · Quick replies</p>
                  </div>
                </a>
                <a data-testid="contact-call-btn" href="tel:+918287651258" className="p-3 rounded-full bg-white border border-border hover:border-primary hover:text-primary transition-colors" aria-label="Call us">
                  <Phone className="h-5 w-5" />
                </a>
              </div>
            </Reveal>
            {INFO.map((c, i) => (
              <Reveal key={c.title} delay={(i + 1) * 90}>
                <div className="contact-card bg-white rounded-2xl p-6 border border-border flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary/10"><c.icon className="h-5 w-5 text-primary" /></div>
                  <div>
                    <p className="font-semibold text-foreground">{c.title}</p>
                    {c.href ? (
                      <a href={c.href} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline">{c.text}</a>
                    ) : (
                      <p className="text-muted-foreground text-sm">{c.text}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
